import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { 
  Plus, Trash2, DollarSign, Receipt, Download, Send, Edit, Save, 
  ClipboardList, FileDown, X, Check, UserPlus, Pencil, CheckCircle, Printer
} from 'lucide-react';
import { COMPANY } from '../../../config/company';
import jsPDF from 'jspdf';

// Contact roles for vendor contacts
const CONTACT_ROLES = {
  homeowner: { label: 'Homeowner', labelEs: 'Propietario', color: '#3b82f6' },
  project_manager: { label: 'Project Manager', labelEs: 'Gerente de Proyecto', color: '#8b5cf6' },
  architect: { label: 'Architect', labelEs: 'Arquitecto', color: '#6366f1' },
  engineer: { label: 'Engineer', labelEs: 'Ingeniero', color: '#14b8a6' },
  inspector: { label: 'Inspector', labelEs: 'Inspector', color: '#f59e0b' },
  hoa_rep: { label: 'HOA Representative', labelEs: 'Representante HOA', color: '#ec4899' },
  tenant: { label: 'Tenant', labelEs: 'Inquilino', color: '#06b6d4' },
  property_manager: { label: 'Property Manager', labelEs: 'Administrador', color: '#10b981' },
  subcontractor: { label: 'Subcontractor', labelEs: 'Subcontratista', color: '#ef4444' },
  supplier: { label: 'Supplier/Vendor', labelEs: 'Proveedor', color: '#f97316' },
  other: { label: 'Other', labelEs: 'Otro', color: '#6b7280' }
};

const ClientAccountingTab = ({
  language,
  client,
  // Summary Data
  accountingSummary,
  accountingPayments,
  accountingCosts,
  accountingInvoices,
  accountingActivities,
  costCategories,
  clientEstimates,
  changeOrders,
  // Contract Amount Edit
  editingContractAmount,
  setEditingContractAmount,
  contractAmountOverride,
  setContractAmountOverride,
  handleSaveContractAmount,
  // Invoice State & Handlers
  showCreateInvoice,
  setShowCreateInvoice,
  newInvoice,
  setNewInvoice,
  editingInvoice,
  setEditingInvoice,
  handleCreateInvoice,
  handleSendInvoice,
  handleDeleteInvoice,
  handleEditInvoice,
  generateInvoicePDF,
  // Change Order State & Handlers
  showCreateChangeOrder,
  setShowCreateChangeOrder,
  newChangeOrder,
  setNewChangeOrder,
  editingChangeOrder,
  setEditingChangeOrder,
  handleCreateChangeOrder,
  handleUpdateChangeOrder,
  handleDeleteChangeOrder,
  handleEditChangeOrderSave,
  addChangeOrderLineItem,
  updateChangeOrderLineItem,
  removeChangeOrderLineItem,
  addEditingChangeOrderLineItem,
  updateEditingChangeOrderLineItem,
  removeEditingChangeOrderLineItem,
  calculateChangeOrderTotal,
  generateChangeOrderPDF,
  // Payment State & Handlers
  showAddPayment,
  setShowAddPayment,
  newPayment,
  setNewPayment,
  handleAddPayment,
  handleDeletePayment,
  // Cost State & Handlers
  showAddCost,
  setShowAddCost,
  newCost,
  setNewCost,
  editingCost,
  setEditingCost,
  handleAddCost,
  handleDeleteCost,
  handleEditCost,
  // Vendor State & Handlers
  vendorSearch,
  handleVendorSearch,
  vendorSearchResults,
  showVendorDropdown,
  setShowVendorDropdown,
  selectVendor,
  showCreateVendor,
  setShowCreateVendor,
  newVendorForm,
  setNewVendorForm,
  createVendorContact,
  // Export
  handleExportReport,
  // Utility
  formatCurrency
}) => {
  // Generate Costs PDF Report
  const generateCostsPDF = () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;
    
    // Header
    pdf.setFillColor(220, 38, 38); // Red header
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY.name || 'Company Name', margin, 15);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(language === 'es' ? 'Reporte de Costos' : 'Costs Report', margin, 25);
    pdf.text(new Date().toLocaleDateString(), pageWidth - margin - 30, 25);
    
    yPos = 45;
    
    // Client Info
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'Cliente' : 'Client', margin, yPos);
    yPos += 7;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.text(client?.name || 'N/A', margin, yPos);
    yPos += 5;
    if (client?.address) pdf.text(client.address, margin, yPos);
    yPos += 5;
    const cityStateZip = [client?.city, client?.state, client?.zip_code].filter(Boolean).join(', ');
    if (cityStateZip) pdf.text(cityStateZip, margin, yPos);
    yPos += 10;
    
    // Summary Box
    pdf.setFillColor(254, 242, 242); // Light red
    pdf.rect(margin, yPos, pageWidth - margin * 2, 25, 'F');
    pdf.setDrawColor(220, 38, 38);
    pdf.rect(margin, yPos, pageWidth - margin * 2, 25, 'S');
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(220, 38, 38);
    pdf.text(language === 'es' ? 'RESUMEN DE COSTOS' : 'COSTS SUMMARY', margin + 5, yPos + 8);
    pdf.setFontSize(16);
    pdf.text(formatCurrency(accountingSummary?.total_costs || 0), margin + 5, yPos + 18);
    
    // Categories summary on the right
    if (accountingSummary?.costs_by_category) {
      let catX = pageWidth / 2;
      let catY = yPos + 8;
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      Object.entries(accountingSummary.costs_by_category).forEach(([cat, amount]) => {
        const catInfo = costCategories.find(c => c.id === cat);
        const catName = catInfo ? (language === 'es' ? catInfo.name_es : catInfo.name) : cat;
        pdf.text(`${catName}: ${formatCurrency(amount)}`, catX, catY);
        catY += 5;
      });
    }
    
    yPos += 35;
    
    // Costs Table Header
    pdf.setFillColor(220, 38, 38);
    pdf.rect(margin, yPos, pageWidth - margin * 2, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'Fecha' : 'Date', margin + 3, yPos + 5.5);
    pdf.text(language === 'es' ? 'Categoría' : 'Category', margin + 28, yPos + 5.5);
    pdf.text(language === 'es' ? 'Descripción' : 'Description', margin + 65, yPos + 5.5);
    pdf.text(language === 'es' ? 'Proveedor' : 'Vendor', margin + 120, yPos + 5.5);
    pdf.text(language === 'es' ? 'Monto' : 'Amount', pageWidth - margin - 25, yPos + 5.5);
    
    yPos += 10;
    
    // Costs Rows
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    
    if (accountingCosts && accountingCosts.length > 0) {
      accountingCosts.forEach((cost, index) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = margin;
        }
        
        // Alternate row color
        if (index % 2 === 0) {
          pdf.setFillColor(254, 249, 249);
          pdf.rect(margin, yPos - 4, pageWidth - margin * 2, 8, 'F');
        }
        
        const catInfo = costCategories.find(c => c.id === cost.category);
        const catName = catInfo ? (language === 'es' ? catInfo.name_es : catInfo.name) : cost.category;
        
        pdf.text(cost.date ? new Date(cost.date).toLocaleDateString() : 'N/A', margin + 3, yPos);
        pdf.text(catName.substring(0, 18), margin + 28, yPos);
        pdf.text((cost.description || '').substring(0, 30), margin + 65, yPos);
        pdf.text((cost.vendor_name || '').substring(0, 20), margin + 120, yPos);
        pdf.text(formatCurrency(cost.amount || 0), pageWidth - margin - 25, yPos);
        
        yPos += 8;
      });
    } else {
      pdf.text(language === 'es' ? 'Sin costos registrados' : 'No costs recorded', margin + 3, yPos);
    }
    
    // Footer
    yPos = pageHeight - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`${language === 'es' ? 'Generado el' : 'Generated on'} ${new Date().toLocaleString()}`, margin, yPos);
    pdf.text(`${COMPANY.name}`, pageWidth - margin - 40, yPos);
    
    // Save
    const fileName = `costs_report_${client?.name?.replace(/\s+/g, '_') || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  // Generate Full Accounting Report PDF
  const generateFullAccountingPDF = () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;
    
    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > pageHeight - 20) {
        pdf.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };
    
    // Header
    pdf.setFillColor(37, 99, 235); // Blue header
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY.name || 'Company Name', margin, 15);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(language === 'es' ? 'Reporte Contable Completo' : 'Full Accounting Report', margin, 25);
    pdf.text(new Date().toLocaleDateString(), pageWidth - margin - 30, 25);
    
    yPos = 45;
    
    // Client Info
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'Cliente' : 'Client', margin, yPos);
    yPos += 7;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.text(client?.name || 'N/A', margin, yPos);
    yPos += 5;
    if (client?.email) pdf.text(client.email, margin, yPos);
    yPos += 5;
    if (client?.phone) pdf.text(client.phone, margin, yPos);
    yPos += 10;
    
    // Summary Section
    pdf.setFillColor(243, 244, 246);
    pdf.rect(margin, yPos, pageWidth - margin * 2, 30, 'F');
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    
    const summaryItems = [
      { label: language === 'es' ? 'Total Contratos' : 'Total Contracts', value: formatCurrency(accountingSummary?.contract_total || 0), color: [37, 99, 235] },
      { label: language === 'es' ? 'Total Pagos' : 'Total Payments', value: formatCurrency(accountingSummary?.total_paid || 0), color: [34, 197, 94] },
      { label: language === 'es' ? 'Total Costos' : 'Total Costs', value: formatCurrency(accountingSummary?.total_costs || 0), color: [239, 68, 68] },
      { label: language === 'es' ? 'Balance' : 'Balance', value: formatCurrency(accountingSummary?.balance || 0), color: [168, 85, 247] },
    ];
    
    const colWidth = (pageWidth - margin * 2) / 4;
    summaryItems.forEach((item, i) => {
      const x = margin + (colWidth * i) + 5;
      pdf.setTextColor(...item.color);
      pdf.text(item.label, x, yPos + 10);
      pdf.setFontSize(14);
      pdf.text(item.value, x, yPos + 20);
      pdf.setFontSize(10);
    });
    
    yPos += 40;
    
    // Invoices Section
    checkPageBreak(50);
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'FACTURAS' : 'INVOICES', margin, yPos);
    yPos += 8;
    
    if (accountingInvoices && accountingInvoices.length > 0) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      accountingInvoices.forEach((inv) => {
        checkPageBreak(12);
        const status = inv.status === 'paid' ? '✓ Paid' : inv.status === 'sent' ? '→ Sent' : '○ Draft';
        pdf.text(`${inv.invoice_number || 'INV'} - ${formatCurrency(inv.amount)} - ${status} - ${inv.due_date || 'N/A'}`, margin + 3, yPos);
        yPos += 6;
      });
    } else {
      pdf.setFontSize(9);
      pdf.text(language === 'es' ? 'Sin facturas' : 'No invoices', margin + 3, yPos);
      yPos += 6;
    }
    yPos += 8;
    
    // Payments Section
    checkPageBreak(50);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'PAGOS RECIBIDOS' : 'PAYMENTS RECEIVED', margin, yPos);
    yPos += 8;
    
    if (accountingPayments && accountingPayments.length > 0) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      accountingPayments.forEach((pmt) => {
        checkPageBreak(12);
        pdf.text(`${pmt.date || 'N/A'} - ${formatCurrency(pmt.amount)} - ${pmt.method || 'N/A'} - ${pmt.description || ''}`, margin + 3, yPos);
        yPos += 6;
      });
    } else {
      pdf.setFontSize(9);
      pdf.text(language === 'es' ? 'Sin pagos' : 'No payments', margin + 3, yPos);
      yPos += 6;
    }
    yPos += 8;
    
    // Costs Section
    checkPageBreak(50);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'COSTOS' : 'COSTS', margin, yPos);
    yPos += 8;
    
    if (accountingCosts && accountingCosts.length > 0) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      accountingCosts.forEach((cost) => {
        checkPageBreak(12);
        const catInfo = costCategories.find(c => c.id === cost.category);
        const catName = catInfo ? (language === 'es' ? catInfo.name_es : catInfo.name) : cost.category;
        pdf.text(`${cost.date || 'N/A'} - ${catName} - ${formatCurrency(cost.amount)} - ${cost.description || ''}`, margin + 3, yPos);
        yPos += 6;
      });
    } else {
      pdf.setFontSize(9);
      pdf.text(language === 'es' ? 'Sin costos' : 'No costs', margin + 3, yPos);
      yPos += 6;
    }
    yPos += 8;
    
    // Change Orders Section
    checkPageBreak(50);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'es' ? 'ÓRDENES DE CAMBIO' : 'CHANGE ORDERS', margin, yPos);
    yPos += 8;
    
    if (changeOrders && changeOrders.length > 0) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      changeOrders.forEach((co) => {
        checkPageBreak(12);
        const status = co.status === 'approved' ? '✓' : co.status === 'rejected' ? '✗' : '○';
        pdf.text(`${status} ${co.change_order_number || 'CO'} - ${formatCurrency(co.total_amount || 0)} - ${co.title || ''}`, margin + 3, yPos);
        yPos += 6;
      });
    } else {
      pdf.setFontSize(9);
      pdf.text(language === 'es' ? 'Sin órdenes de cambio' : 'No change orders', margin + 3, yPos);
      yPos += 6;
    }
    
    // Footer on last page
    yPos = pageHeight - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`${language === 'es' ? 'Generado el' : 'Generated on'} ${new Date().toLocaleString()}`, margin, yPos);
    pdf.text(`${COMPANY.name}`, pageWidth - margin - 40, yPos);
    
    // Save
    const fileName = `accounting_report_${client?.name?.replace(/\s+/g, '_') || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">
          {language === 'es' ? 'Contabilidad del Cliente' : 'Client Accounting'}
        </h2>
        <Button onClick={generateFullAccountingPDF} variant="outline" className="flex items-center gap-2" data-testid="export-full-accounting-pdf">
          <FileDown className="h-4 w-4" />
          {language === 'es' ? 'Exportar Reporte PDF' : 'Export PDF Report'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{language === 'es' ? 'Total Contratos' : 'Total Contracts'}</p>
              {!editingContractAmount && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-6 w-6 p-0"
                  onClick={() => {
                    setContractAmountOverride(client?.contract_amount_override || accountingSummary?.contract_total || 0);
                    setEditingContractAmount(true);
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </div>
            {editingContractAmount ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-bold text-blue-600">$</span>
                <Input
                  type="number"
                  value={contractAmountOverride}
                  onChange={(e) => setContractAmountOverride(e.target.value)}
                  className="h-8 w-28 text-lg font-bold"
                  autoFocus
                />
                <Button size="sm" className="h-8 px-2" onClick={handleSaveContractAmount}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingContractAmount(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(client?.contract_amount_override || accountingSummary?.contract_total || 0)}
              </p>
            )}
            <p className="text-xs text-gray-400">{accountingSummary?.projects_count || 0} {language === 'es' ? 'proyectos' : 'projects'}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">{language === 'es' ? 'Pagos Recibidos' : 'Payments Received'}</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(accountingSummary?.total_payments || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">{language === 'es' ? 'Saldo Pendiente' : 'Outstanding Balance'}</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(accountingSummary?.outstanding_balance || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">{language === 'es' ? 'Total Costos' : 'Total Costs'}</p>
            <p className="text-2xl font-bold text-orange-600">-{formatCurrency(accountingSummary?.total_costs || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">{language === 'es' ? 'Ganancia' : 'Profit'}</p>
            <p className={`text-2xl font-bold ${(accountingSummary?.profit || 0) >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
              {formatCurrency(accountingSummary?.profit || 0)}
            </p>
            <p className="text-xs text-gray-400">
              {accountingSummary?.profit_margin ? `${accountingSummary.profit_margin.toFixed(1)}% margin` : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Section */}
      <Card>
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center"><Receipt className="mr-2 h-5 w-5" />{language === 'es' ? 'Facturas' : 'Invoices'}</span>
            <Button size="sm" variant="secondary" onClick={() => setShowCreateInvoice(true)}>
              <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Crear Factura' : 'Create Invoice'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {showCreateInvoice && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-3">{language === 'es' ? 'Nueva Factura' : 'New Invoice'}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
                  <Input value={newInvoice.description} onChange={(e) => setNewInvoice(prev => ({ ...prev, description: e.target.value }))} placeholder="Services Rendered" />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Monto' : 'Amount'}</Label>
                  <Input type="number" value={newInvoice.amount} onChange={(e) => setNewInvoice(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleCreateInvoice} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                  <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Crear' : 'Create'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCreateInvoice(false)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
              </div>
            </div>
          )}
          
          {accountingInvoices.length > 0 ? (
            <div className="space-y-3">
              {accountingInvoices.map((invoice) => (
                <div key={invoice.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  {editingInvoice?.id === invoice.id ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3">{language === 'es' ? 'Editar Factura' : 'Edit Invoice'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
                          <Input 
                            value={editingInvoice.description || ''} 
                            onChange={(e) => setEditingInvoice(prev => ({ ...prev, description: e.target.value }))} 
                          />
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Monto' : 'Amount'}</Label>
                          <Input 
                            type="number" 
                            value={editingInvoice.amount || ''} 
                            onChange={(e) => setEditingInvoice(prev => ({ ...prev, amount: e.target.value }))} 
                          />
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Estado' : 'Status'}</Label>
                          <Select value={editingInvoice.status} onValueChange={(v) => setEditingInvoice(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">{language === 'es' ? 'Borrador' : 'Draft'}</SelectItem>
                              <SelectItem value="sent">{language === 'es' ? 'Enviada' : 'Sent'}</SelectItem>
                              <SelectItem value="accepted">{language === 'es' ? 'Aceptada' : 'Accepted'}</SelectItem>
                              <SelectItem value="paid">{language === 'es' ? 'Pagada' : 'Paid'}</SelectItem>
                              <SelectItem value="declined">{language === 'es' ? 'Rechazada' : 'Declined'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={handleEditInvoice} className="bg-blue-600 text-white hover:bg-blue-700">
                          <Save className="h-4 w-4 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingInvoice(null)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{invoice.invoice_number}</p>
                          <p className="text-sm text-gray-500">{invoice.description}</p>
                          <p className="text-xs text-gray-400">{invoice.invoice_date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">{formatCurrency(invoice.amount)}</p>
                          <Badge className={
                            invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                            invoice.status === 'declined' ? 'bg-red-100 text-red-700' :
                            invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {invoice.status === 'paid' ? (language === 'es' ? 'Pagada' : 'Paid') :
                             invoice.status === 'accepted' ? (language === 'es' ? 'Aceptada' : 'Accepted') :
                             invoice.status === 'declined' ? (language === 'es' ? 'Rechazada' : 'Declined') :
                             invoice.status === 'sent' ? (language === 'es' ? 'Enviada' : 'Sent') :
                             (language === 'es' ? 'Borrador' : 'Draft')}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {invoice.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => handleSendInvoice(invoice.id)}>
                            <Send className="h-3 w-3 mr-1" />{language === 'es' ? 'Enviar' : 'Send'}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => generateInvoicePDF(invoice)}>
                          <FileDown className="h-3 w-3 mr-1" />PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingInvoice({...invoice})}>
                          <Edit className="h-3 w-3 mr-1" />{language === 'es' ? 'Editar' : 'Edit'}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteInvoice(invoice.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">{language === 'es' ? 'No hay facturas.' : 'No invoices yet.'}</p>
          )}
        </CardContent>
      </Card>

      {/* Change Orders Section */}
      <Card>
        <CardHeader className="bg-purple-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center"><ClipboardList className="mr-2 h-5 w-5" />{language === 'es' ? 'Órdenes de Cambio' : 'Change Orders'}</span>
            <Button size="sm" variant="secondary" onClick={() => setShowCreateChangeOrder(true)}>
              <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Nueva Orden' : 'New Order'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Create Change Order Form */}
          {showCreateChangeOrder && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-3">{language === 'es' ? 'Crear Orden de Cambio' : 'Create Change Order'}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Descripción' : 'Description'} *</Label>
                  <Input 
                    value={newChangeOrder.description} 
                    onChange={(e) => setNewChangeOrder(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={language === 'es' ? 'Ej: Agregar claraboya' : 'e.g., Add skylight'}
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Proyecto' : 'Project'}</Label>
                  <Select value={newChangeOrder.project_name} onValueChange={(v) => setNewChangeOrder(prev => ({ ...prev, project_name: v }))}>
                    <SelectTrigger><SelectValue placeholder="--" /></SelectTrigger>
                    <SelectContent>
                      {clientEstimates.map(est => (
                        <SelectItem key={est.id} value={est.proposal_id || est.estimate_number}>
                          {est.proposal_id || est.estimate_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>{language === 'es' ? 'Razón del Cambio' : 'Reason for Change'}</Label>
                  <Textarea 
                    value={newChangeOrder.reason} 
                    onChange={(e) => setNewChangeOrder(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={language === 'es' ? 'Ej: Cliente solicitó claraboya adicional en cocina' : 'e.g., Client requested additional skylight in kitchen'}
                    className="h-16"
                  />
                </div>
              </div>
              
              {/* Line Items */}
              <div className="mt-4">
                <Label className="font-semibold">{language === 'es' ? 'Partidas' : 'Line Items'}</Label>
                <div className="space-y-2 mt-2">
                  {newChangeOrder.line_items.map((item) => (
                    <div key={item.id} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-6">
                        <Input 
                          placeholder={language === 'es' ? 'Descripción' : 'Description'}
                          value={item.description}
                          onChange={(e) => updateChangeOrderLineItem(item.id, 'description', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input 
                          type="number" 
                          placeholder={language === 'es' ? 'Cant.' : 'Qty'}
                          value={item.quantity}
                          onChange={(e) => updateChangeOrderLineItem(item.id, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input 
                          type="number" 
                          placeholder={language === 'es' ? 'Precio' : 'Price'}
                          value={item.unit_price}
                          onChange={(e) => updateChangeOrderLineItem(item.id, 'unit_price', e.target.value)}
                          step="0.01"
                        />
                      </div>
                      <div className="col-span-1 text-right font-semibold text-sm">
                        {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                      </div>
                      <div className="col-span-1">
                        <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => removeChangeOrderLineItem(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={addChangeOrderLineItem}>
                  <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Agregar Partida' : 'Add Line'}
                </Button>
              </div>
              
              {/* Total and Actions */}
              <div className="mt-4 flex justify-between items-center">
                <div className="text-lg font-bold">
                  Total: <span className="text-purple-600">{formatCurrency(calculateChangeOrderTotal(newChangeOrder.line_items))}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateChangeOrder} className="bg-purple-600 text-white hover:bg-purple-700">
                    <Save className="h-4 w-4 mr-1" />{language === 'es' ? 'Crear' : 'Create'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCreateChangeOrder(false)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                </div>
              </div>
            </div>
          )}

          {/* Change Orders List */}
          {changeOrders.length > 0 ? (
            <div className="space-y-3">
              {changeOrders.map((co) => (
                <div key={co.id} className="border rounded-lg p-4 hover:bg-purple-50">
                  {editingChangeOrder?.id === co.id ? (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3">{language === 'es' ? 'Editar Orden de Cambio' : 'Edit Change Order'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>{language === 'es' ? 'Descripción' : 'Description'} *</Label>
                          <Input 
                            value={editingChangeOrder.description} 
                            onChange={(e) => setEditingChangeOrder(prev => ({ ...prev, description: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Proyecto' : 'Project'}</Label>
                          <Select value={editingChangeOrder.project_name || ''} onValueChange={(v) => setEditingChangeOrder(prev => ({ ...prev, project_name: v }))}>
                            <SelectTrigger><SelectValue placeholder="--" /></SelectTrigger>
                            <SelectContent>
                              {clientEstimates.map(est => (
                                <SelectItem key={est.id} value={est.proposal_id || est.estimate_number}>
                                  {est.proposal_id || est.estimate_number}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2">
                          <Label>{language === 'es' ? 'Razón del Cambio' : 'Reason for Change'}</Label>
                          <Textarea 
                            value={editingChangeOrder.reason || ''} 
                            onChange={(e) => setEditingChangeOrder(prev => ({ ...prev, reason: e.target.value }))}
                            className="h-16"
                          />
                        </div>
                      </div>
                      
                      {/* Line Items Edit */}
                      <div className="mt-4">
                        <Label className="font-semibold">{language === 'es' ? 'Partidas' : 'Line Items'}</Label>
                        <div className="space-y-2 mt-2">
                          {(editingChangeOrder.line_items || []).map((item, idx) => (
                            <div key={item.id || idx} className="grid grid-cols-12 gap-2 items-center">
                              <div className="col-span-6">
                                <Input 
                                  placeholder={language === 'es' ? 'Descripción' : 'Description'}
                                  value={item.description || ''}
                                  onChange={(e) => updateEditingChangeOrderLineItem(item.id, 'description', e.target.value)}
                                />
                              </div>
                              <div className="col-span-2">
                                <Input 
                                  type="number" 
                                  placeholder={language === 'es' ? 'Cant.' : 'Qty'}
                                  value={item.quantity || ''}
                                  onChange={(e) => updateEditingChangeOrderLineItem(item.id, 'quantity', e.target.value)}
                                />
                              </div>
                              <div className="col-span-2">
                                <Input 
                                  type="number" 
                                  placeholder={language === 'es' ? 'Precio' : 'Price'}
                                  value={item.unit_price || ''}
                                  onChange={(e) => updateEditingChangeOrderLineItem(item.id, 'unit_price', e.target.value)}
                                  step="0.01"
                                />
                              </div>
                              <div className="col-span-1 text-right font-semibold text-sm">
                                {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                              </div>
                              <div className="col-span-1">
                                <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => removeEditingChangeOrderLineItem(item.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button size="sm" variant="outline" className="mt-2" onClick={addEditingChangeOrderLineItem}>
                          <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Agregar Partida' : 'Add Line'}
                        </Button>
                      </div>
                      
                      {/* Total and Actions */}
                      <div className="mt-4 flex justify-between items-center">
                        <div className="text-lg font-bold">
                          Total: <span className="text-purple-600">{formatCurrency(calculateChangeOrderTotal(editingChangeOrder.line_items || []))}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleEditChangeOrderSave} className="bg-purple-600 text-white hover:bg-purple-700">
                            <Save className="h-4 w-4 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingChangeOrder(null)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{co.change_order_number}</p>
                          <p className="text-sm text-gray-700">{co.description}</p>
                          {co.reason && <p className="text-xs text-gray-500 mt-1">{language === 'es' ? 'Razón' : 'Reason'}: {co.reason}</p>}
                          <p className="text-xs text-gray-400 mt-1">{co.date} {co.project_name && `• ${co.project_name}`}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-purple-600">{formatCurrency(co.total_amount)}</p>
                          <Badge className={
                            co.status === 'approved' ? 'bg-green-100 text-green-700' :
                            co.status === 'declined' ? 'bg-red-100 text-red-700' :
                            co.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {co.status === 'approved' ? (language === 'es' ? 'Aprobada' : 'Approved') :
                             co.status === 'declined' ? (language === 'es' ? 'Rechazada' : 'Declined') :
                             co.status === 'sent' ? (language === 'es' ? 'Enviada' : 'Sent') :
                             (language === 'es' ? 'Borrador' : 'Draft')}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Line Items Preview */}
                      {co.line_items && co.line_items.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-semibold text-gray-500 mb-2">{language === 'es' ? 'Partidas' : 'Line Items'}:</p>
                          <div className="space-y-1">
                            {co.line_items.slice(0, 3).map((item, idx) => (
                              <div key={idx} className="text-sm flex justify-between text-gray-600">
                                <span>{item.description}</span>
                                <span>{formatCurrency((item.quantity || 1) * (item.unit_price || 0))}</span>
                              </div>
                            ))}
                            {co.line_items.length > 3 && (
                              <p className="text-xs text-gray-400">+{co.line_items.length - 3} {language === 'es' ? 'más' : 'more'}...</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {co.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateChangeOrder(co.id, { status: 'sent' })}>
                            <Send className="h-3 w-3 mr-1" />{language === 'es' ? 'Enviar' : 'Send'}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => generateChangeOrderPDF(co)}>
                          <FileDown className="h-3 w-3 mr-1" />PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingChangeOrder({...co, line_items: co.line_items?.map((item, i) => ({...item, id: item.id || Date.now() + i})) || []})}>
                          <Edit className="h-3 w-3 mr-1" />{language === 'es' ? 'Editar' : 'Edit'}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteChangeOrder(co.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {/* Additional status actions */}
                      {(co.status === 'draft' || co.status === 'sent') && (
                        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                          <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleUpdateChangeOrder(co.id, { status: 'approved' })}>
                            <CheckCircle className="h-3 w-3 mr-1" />{language === 'es' ? 'Aprobar' : 'Approve'}
                          </Button>
                          {co.status === 'sent' && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateChangeOrder(co.id, { status: 'declined' })}>
                              <X className="h-3 w-3 mr-1" />{language === 'es' ? 'Rechazar' : 'Decline'}
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">{language === 'es' ? 'No hay órdenes de cambio.' : 'No change orders yet.'}</p>
          )}
        </CardContent>
      </Card>

      {/* Payments Section */}
      <Card>
        <CardHeader className="bg-green-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center"><DollarSign className="mr-2 h-5 w-5" />{language === 'es' ? 'Pagos Recibidos' : 'Payments Received'}</span>
            <Button size="sm" variant="secondary" onClick={() => setShowAddPayment(true)}>
              <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Agregar Pago' : 'Add Payment'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {showAddPayment && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-3">{language === 'es' ? 'Registrar Pago' : 'Record Payment'}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Monto' : 'Amount'} *</Label>
                  <Input type="number" value={newPayment.amount} onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Fecha' : 'Date'} *</Label>
                  <Input type="date" value={newPayment.payment_date} onChange={(e) => setNewPayment(prev => ({ ...prev, payment_date: e.target.value }))} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Método' : 'Method'}</Label>
                  <Select value={newPayment.payment_method} onValueChange={(v) => setNewPayment(prev => ({ ...prev, payment_method: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check">{language === 'es' ? 'Cheque' : 'Check'}</SelectItem>
                      <SelectItem value="cash">{language === 'es' ? 'Efectivo' : 'Cash'}</SelectItem>
                      <SelectItem value="credit_card">{language === 'es' ? 'Tarjeta' : 'Credit Card'}</SelectItem>
                      <SelectItem value="bank_transfer">{language === 'es' ? 'Transferencia' : 'Bank Transfer'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'es' ? 'Proyecto' : 'Project'}</Label>
                  <Select value={newPayment.project_name} onValueChange={(v) => setNewPayment(prev => ({ ...prev, project_name: v }))}>
                    <SelectTrigger><SelectValue placeholder="--" /></SelectTrigger>
                    <SelectContent>
                      {clientEstimates.map(est => (
                        <SelectItem key={est.id} value={est.proposal_id || est.estimate_number}>
                          {est.proposal_id || est.estimate_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label>{language === 'es' ? 'Notas' : 'Notes'}</Label>
                <Input value={newPayment.notes} onChange={(e) => setNewPayment(prev => ({ ...prev, notes: e.target.value }))} placeholder={language === 'es' ? 'Ej: Depósito inicial' : 'e.g., Initial deposit'} />
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleAddPayment} className="bg-green-600 text-white hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddPayment(false)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
              </div>
            </div>
          )}
          
          {accountingPayments.length > 0 ? (
            <div className="space-y-2">
              {accountingPayments.map((payment) => (
                <div key={payment.id} className="flex justify-between items-center border-b py-2">
                  <div>
                    <p className="font-semibold text-green-600">+{formatCurrency(payment.amount)}</p>
                    <p className="text-xs text-gray-500">
                      {payment.payment_date} • {payment.payment_method}
                      {payment.project_name && ` • ${payment.project_name}`}
                    </p>
                    {payment.notes && <p className="text-xs text-gray-400">{payment.notes}</p>}
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeletePayment(payment.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">{language === 'es' ? 'Sin pagos registrados' : 'No payments recorded'}</p>
          )}
        </CardContent>
      </Card>

      {/* Costs Section */}
      <Card>
        <CardHeader className="bg-red-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center"><Receipt className="mr-2 h-5 w-5" />{language === 'es' ? 'Costos del Cliente' : 'Client Costs'}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm bg-red-700 px-2 py-1 rounded">
                {language === 'es' ? 'Total' : 'Total'}: {formatCurrency(accountingSummary?.total_costs || 0)}
              </span>
              <Button size="sm" variant="secondary" onClick={generateCostsPDF} data-testid="print-costs-pdf">
                <Printer className="h-4 w-4 mr-1" />{language === 'es' ? 'PDF' : 'PDF'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowAddCost(true)}>
                <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Agregar' : 'Add Cost'}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {showAddCost && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-3">{language === 'es' ? 'Registrar Costo' : 'Record Cost'}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Categoría' : 'Category'}</Label>
                  <Select value={newCost.category} onValueChange={(v) => setNewCost(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {costCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {language === 'es' ? cat.name_es : cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'es' ? 'Monto' : 'Amount'} *</Label>
                  <Input type="number" value={newCost.amount} onChange={(e) => setNewCost(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Fecha' : 'Date'} *</Label>
                  <Input type="date" value={newCost.date} onChange={(e) => setNewCost(prev => ({ ...prev, date: e.target.value }))} />
                </div>
                <div className="col-span-2 md:col-span-3">
                  <Label>{language === 'es' ? 'Descripción' : 'Description'} *</Label>
                  <Input value={newCost.description} onChange={(e) => setNewCost(prev => ({ ...prev, description: e.target.value }))} placeholder={language === 'es' ? 'Ej: Permiso' : 'e.g., Building permit'} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Proveedor' : 'Vendor'}</Label>
                  <div className="relative">
                    <Input 
                      value={vendorSearch} 
                      onChange={(e) => handleVendorSearch(e.target.value)}
                      onFocus={() => vendorSearch.length > 0 && setShowVendorDropdown(true)}
                      placeholder={language === 'es' ? 'Buscar o crear proveedor...' : 'Search or create vendor...'}
                      className={newCost.vendor_contact_id ? 'border-green-500 bg-green-50' : ''}
                    />
                    {newCost.vendor_contact_id && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-600 text-xs">✓ Linked</span>
                    )}
                    
                    {/* Vendor Search Dropdown */}
                    {showVendorDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {vendorSearchResults.length > 0 ? (
                          <>
                            {vendorSearchResults.map(vendor => (
                              <div 
                                key={vendor.id}
                                className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                                onClick={() => selectVendor(vendor)}
                              >
                                <div>
                                  <p className="font-medium">{vendor.company || vendor.name}</p>
                                  <p className="text-xs text-gray-500">{vendor.name}{vendor.phone ? ` • ${vendor.phone}` : ''}</p>
                                </div>
                                <Badge className="text-xs" style={{ backgroundColor: CONTACT_ROLES[vendor.role]?.color || '#6b7280', color: 'white' }}>
                                  {language === 'es' ? CONTACT_ROLES[vendor.role]?.labelEs : CONTACT_ROLES[vendor.role]?.label}
                                </Badge>
                              </div>
                            ))}
                            <div className="border-t" />
                          </>
                        ) : vendorSearch.length > 0 && (
                          <p className="p-2 text-gray-500 text-sm">{language === 'es' ? 'No se encontró proveedor' : 'No vendor found'}</p>
                        )}
                        {/* Create New Option */}
                        <div 
                          className="p-2 hover:bg-blue-50 cursor-pointer flex items-center gap-2 text-blue-600 border-t"
                          onClick={() => {
                            setNewVendorForm(prev => ({ ...prev, company: vendorSearch, name: '' }));
                            setShowCreateVendor(true);
                            setShowVendorDropdown(false);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          <span>{language === 'es' ? 'Crear nuevo: ' : 'Create new: '}<strong>"{vendorSearch || '...'}"</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>{language === 'es' ? 'Proyecto' : 'Project'}</Label>
                  <Select value={newCost.project_name} onValueChange={(v) => setNewCost(prev => ({ ...prev, project_name: v }))}>
                    <SelectTrigger><SelectValue placeholder="--" /></SelectTrigger>
                    <SelectContent>
                      {clientEstimates.map(est => (
                        <SelectItem key={est.id} value={est.proposal_id || est.estimate_number}>
                          {est.proposal_id || est.estimate_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleAddCost} className="bg-red-600 text-white hover:bg-red-700">
                  <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowAddCost(false); }}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
              </div>
            </div>
          )}

          {/* Create Vendor Contact Modal */}
          {showCreateVendor && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setShowCreateVendor(false)}>
              <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    {language === 'es' ? 'Crear Contacto de Proveedor' : 'Create Vendor Contact'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div>
                    <Label>{language === 'es' ? 'Empresa/Negocio' : 'Company/Business'}</Label>
                    <Input 
                      value={newVendorForm.company} 
                      onChange={(e) => setNewVendorForm(prev => ({ ...prev, company: e.target.value }))}
                      placeholder={language === 'es' ? 'Ej: Home Depot, Naples' : 'e.g., Home Depot, Naples'}
                    />
                  </div>
                  <div>
                    <Label>{language === 'es' ? 'Nombre de Contacto' : 'Contact Name'}</Label>
                    <Input 
                      value={newVendorForm.name} 
                      onChange={(e) => setNewVendorForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={language === 'es' ? 'Ej: Jose Ramirez' : 'e.g., Jose Ramirez'}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                      <Input 
                        value={newVendorForm.phone} 
                        onChange={(e) => setNewVendorForm(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={newVendorForm.email} 
                        onChange={(e) => setNewVendorForm(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{language === 'es' ? 'Tipo' : 'Type'}</Label>
                    <Select value={newVendorForm.role} onValueChange={(v) => setNewVendorForm(prev => ({ ...prev, role: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="supplier">{language === 'es' ? 'Proveedor/Tienda' : 'Supplier/Store'}</SelectItem>
                        <SelectItem value="subcontractor">{language === 'es' ? 'Subcontratista' : 'Subcontractor'}</SelectItem>
                        <SelectItem value="other">{language === 'es' ? 'Otro' : 'Other'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={createVendorContact} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                      <UserPlus className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'Crear y Vincular' : 'Create & Link'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowCreateVendor(false)}>
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Costs by Category Summary */}
          {accountingSummary?.costs_by_category && Object.keys(accountingSummary.costs_by_category).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {Object.entries(accountingSummary.costs_by_category).map(([cat, amount]) => {
                const category = costCategories.find(c => c.id === cat);
                return (
                  <div key={cat} className="bg-gray-50 rounded p-2 text-center">
                    <p className="text-lg">{category?.icon || '📦'}</p>
                    <p className="text-xs text-gray-500">{language === 'es' ? (category?.name_es || cat) : (category?.name || cat)}</p>
                    <p className="font-semibold text-red-600">{formatCurrency(amount)}</p>
                  </div>
                );
              })}
            </div>
          )}
          
          {accountingCosts.length > 0 ? (
            <div className="space-y-2">
              {accountingCosts.map((cost) => {
                const category = costCategories.find(c => c.id === cost.category);
                
                if (editingCost?.id === cost.id) {
                  return (
                    <div key={cost.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-2">
                      <h4 className="font-semibold mb-3">{language === 'es' ? 'Editar Costo' : 'Edit Cost'}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <Label>{language === 'es' ? 'Categoría' : 'Category'}</Label>
                          <Select value={editingCost.category} onValueChange={(v) => setEditingCost(prev => ({ ...prev, category: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {costCategories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  {cat.icon} {language === 'es' ? cat.name_es : cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Monto' : 'Amount'} *</Label>
                          <Input type="number" value={editingCost.amount} onChange={(e) => setEditingCost(prev => ({ ...prev, amount: e.target.value }))} />
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Fecha' : 'Date'} *</Label>
                          <Input type="date" value={editingCost.date} onChange={(e) => setEditingCost(prev => ({ ...prev, date: e.target.value }))} />
                        </div>
                        <div className="col-span-2 md:col-span-3">
                          <Label>{language === 'es' ? 'Descripción' : 'Description'} *</Label>
                          <Input value={editingCost.description} onChange={(e) => setEditingCost(prev => ({ ...prev, description: e.target.value }))} />
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Proveedor' : 'Vendor'}</Label>
                          <Input value={editingCost.vendor || ''} onChange={(e) => setEditingCost(prev => ({ ...prev, vendor: e.target.value }))} />
                        </div>
                        <div>
                          <Label>{language === 'es' ? 'Proyecto' : 'Project'}</Label>
                          <Select value={editingCost.project_name || ''} onValueChange={(v) => setEditingCost(prev => ({ ...prev, project_name: v }))}>
                            <SelectTrigger><SelectValue placeholder="--" /></SelectTrigger>
                            <SelectContent>
                              {clientEstimates.map(est => (
                                <SelectItem key={est.id} value={est.proposal_id || est.estimate_number}>
                                  {est.proposal_id || est.estimate_number}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={handleEditCost} className="bg-orange-600 text-white hover:bg-orange-700">
                          <Save className="h-4 w-4 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingCost(null)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div key={cost.id} className="flex justify-between items-center border-b py-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{category?.icon || '📦'}</span>
                      <div>
                        <p className="font-semibold">{cost.description}</p>
                        <p className="text-xs text-gray-500">
                          {cost.date} • {language === 'es' ? (category?.name_es || cost.category) : (category?.name || cost.category)}
                          {cost.project_name && ` • ${cost.project_name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-red-600">-{formatCurrency(cost.amount)}</p>
                      <Button size="sm" variant="ghost" className="text-blue-500" onClick={() => setEditingCost({...cost})}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteCost(cost.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">{language === 'es' ? 'Sin costos registrados' : 'No costs recorded'}</p>
          )}
        </CardContent>
      </Card>

      {/* Activity Log */}
      <Card>
        <CardHeader className="bg-gray-700 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center"><ClipboardList className="mr-2 h-5 w-5" />{language === 'es' ? 'Registro de Actividades' : 'Activity Log'}</span>
            <Button size="sm" variant="secondary" onClick={() => handleExportReport('excel')}>
              <Download className="h-4 w-4 mr-1" />{language === 'es' ? 'Exportar Reporte' : 'Export Report'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {accountingActivities.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {accountingActivities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 text-sm border-l-2 border-gray-300 pl-3 py-1">
                  <span className="text-lg">
                    {activity.action_type === 'payment' ? '💰' :
                     activity.action_type === 'cost' ? '📉' :
                     activity.action_type === 'invoice_created' ? '📄' :
                     activity.action_type === 'invoice_sent' ? '📧' : '📝'}
                  </span>
                  <div className="flex-1">
                    <p className="text-gray-700">{activity.description}</p>
                    <p className="text-xs text-gray-400">{new Date(activity.created_at).toLocaleString()}</p>
                  </div>
                  {activity.amount > 0 && (
                    <span className={`font-semibold ${activity.action_type === 'payment' ? 'text-green-600' : activity.action_type === 'cost' ? 'text-red-600' : 'text-gray-600'}`}>
                      {formatCurrency(activity.amount)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">{language === 'es' ? 'Sin actividades' : 'No activities recorded'}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientAccountingTab;
